/**
 * FormStyle defines the visual customization options for a web form.
 * These styles are applied to both the form editor preview and the public-facing form.
 */
export interface FormStyle {
	// Colors
	primaryColor: string; // Button and accent color (e.g., "#1976D2")
	backgroundColor: string; // Form background color (e.g., "#FFFFFF")
	textColor: string; // Primary text color (e.g., "#212121")
	fieldBackgroundColor: string; // Input field background (e.g., "#FFFFFF")
	fieldBorderColor: string; // Input field border color (e.g., "#E0E0E0")
	errorTextColor: string; // Error message text color (e.g., "#f44336")

	// Typography
	fontFamily: string; // Font family (e.g., "Inter", "Roboto", "system-ui")
	fontSize: "small" | "medium" | "large"; // Content text size (header/footer)
	headingSize: "small" | "medium" | "large"; // Heading size preset
	labelFontSize: "small" | "medium" | "large"; // Field label text size
	footerFontSize: "small" | "medium" | "large"; // Footer content text size

	// Spacing
	fieldSpacing: "compact" | "normal" | "relaxed"; // Gap between fields
	fieldPadding: "compact" | "normal" | "relaxed"; // Padding inside input fields

	// Borders
	formBorderRadius: "none" | "small" | "medium" | "large"; // Form card corner rounding
	borderRadius: "none" | "small" | "medium" | "large"; // Field corner rounding
	borderWidth: "thin" | "medium" | "thick"; // Border thickness

	// Button style
	buttonStyle: "filled" | "outlined" | "text"; // Submit button appearance
	buttonTextColor: string; // Submit button label color (e.g., "#FFFFFF")
	buttonRadius: "square" | "rounded" | "pill"; // Button corner style
	buttonText: string; // Text to show in submit button

	// Logo
	logoUrl?: string; // Optional logo image URL
	logoPosition: "left" | "center" | "right"; // Logo alignment

	// Rich content (HTML)
	headerHtml?: string; // Custom header content (sanitized HTML)
	footerHtml?: string; // Custom footer content (sanitized HTML)
}

/**
 * Default form style values
 */
export const DEFAULT_FORM_STYLE: FormStyle = {
	// Colors
	primaryColor: "#1976D2",
	backgroundColor: "#FFFFFF",
	textColor: "#212121",
	fieldBackgroundColor: "#FFFFFF",
	fieldBorderColor: "#E0E0E0",
	errorTextColor: "#f44336",

	// Typography
	fontFamily: "Inter",
	fontSize: "medium",
	headingSize: "medium",
	labelFontSize: "medium",
	footerFontSize: "medium",

	// Spacing
	fieldSpacing: "normal",
	fieldPadding: "normal",

	// Borders
	formBorderRadius: "medium",
	borderRadius: "medium",
	borderWidth: "thin",

	// Button
	buttonStyle: "filled",
	buttonTextColor: "#FFFFFF",
	buttonRadius: "rounded",
	buttonText: "Take a Video and/or Photos",
	// Logo
	logoUrl: undefined,
	logoPosition: "center",

	// Content
	headerHtml: undefined,
	footerHtml: undefined
};

/**
 * Available font families for form styling
 */
export const FONT_FAMILIES = [
	{ text: "Inter", value: "Inter" },
	{ text: "Roboto", value: "Roboto" },
	{ text: "Open Sans", value: "Open Sans" },
	{ text: "Lato", value: "Lato" },
	{ text: "Montserrat", value: "Montserrat" },
	{ text: "Poppins", value: "Poppins" },
	{ text: "Source Sans Pro", value: "Source Sans Pro" },
	{ text: "System Default", value: "system-ui" }
];

/**
 * Maps style presets to actual CSS values
 */
export const STYLE_VALUES = {
	fontSize: {
		small: "14px",
		medium: "16px",
		large: "18px"
	},
	headingSize: {
		small: "20px",
		medium: "24px",
		large: "32px"
	},
	labelFontSize: {
		small: "12px",
		medium: "14px",
		large: "16px"
	},
	footerFontSize: {
		small: "14px",
		medium: "16px",
		large: "18px"
	},
	fieldSpacing: {
		compact: "12px",
		normal: "20px",
		relaxed: "28px"
	},
	fieldPadding: {
		compact: "8px 10px",
		normal: "12px 14px",
		relaxed: "16px 18px"
	},
	formBorderRadius: {
		none: "0px",
		small: "8px",
		medium: "16px",
		large: "24px"
	},
	borderRadius: {
		none: "0px",
		small: "4px",
		medium: "8px",
		large: "16px"
	},
	borderWidth: {
		thin: "1px",
		medium: "2px",
		thick: "3px"
	},
	buttonRadius: {
		square: "4px",
		rounded: "8px",
		pill: "50px"
	}
};

/**
 * Converts a FormStyle object to CSS custom properties
 */
export function formStyleToCssVars(style: FormStyle): Record<string, string> {
	return {
		"--form-primary-color": style.primaryColor,
		"--form-button-text-color": style.buttonTextColor,
		"--form-background-color": style.backgroundColor,
		"--form-text-color": style.textColor,
		"--form-field-background": style.fieldBackgroundColor,
		"--form-field-border-color": style.fieldBorderColor,
		"--form-error-text-color": style.errorTextColor,
		"--form-font-family": style.fontFamily,
		"--form-font-size": STYLE_VALUES.fontSize[style.fontSize],
		"--form-heading-size": STYLE_VALUES.headingSize[style.headingSize],
		"--form-label-font-size": STYLE_VALUES.labelFontSize[style.labelFontSize],
		"--form-footer-font-size": STYLE_VALUES.footerFontSize[style.footerFontSize],
		"--form-field-spacing": STYLE_VALUES.fieldSpacing[style.fieldSpacing],
		"--form-field-padding": STYLE_VALUES.fieldPadding[style.fieldPadding],
		"--form-form-border-radius": STYLE_VALUES.formBorderRadius[style.formBorderRadius],
		"--form-border-radius": STYLE_VALUES.borderRadius[style.borderRadius],
		"--form-border-width": STYLE_VALUES.borderWidth[style.borderWidth],
		"--form-button-radius": STYLE_VALUES.buttonRadius[style.buttonRadius]
	};
}
